import SmsCombinedPanel from "@/components/SmsCombinedPanel";
import BookkeeperHistoryPanel from "@/components/BookkeeperHistoryPanel";
/**
 * APX WorkCenter — Bookkeeping Manager Dashboard (Jany)
 * Design: "Gold Standard" — Premium Editorial Finance
 *
 * Layout:
 *   1. Header — greeting, KPIs, action buttons
 *   2. Two-column body:
 *      Left  — Client Roster with T1/T2/T3/C-P&L tier assignment grid
 *      Right — Team Workload (per-tier counts) + Schedule + Inbox
 *
 * Tier system:
 *   T1 = Bank Feed (B)   — assignable to any bookkeeper
 *   T2 = Coding (C)      — assignable to any bookkeeper
 *   T3 = ATB             — assignable to any bookkeeper
 *   T3-C = P&L Done      — Jany/Irit/Gus only (restricted)
 *   Default assignee for all unassigned tasks = Jany
 */

import { useState, useMemo, useRef, useEffect } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useEffectiveRole, usePreview } from "@/contexts/PreviewContext";
import { trpc } from "@/lib/trpc";
import AllCommunicationsFeed from "@/components/AllCommunicationsFeed";
import { toast } from "sonner";
import SendNotificationModal from "@/components/SendNotificationModal";
import FileDropZone from "@/components/FileDropZone";
import ClientDocuments from "@/components/ClientDocuments";
import { ContactClientModal } from "@/components/ContactClientModal";
import XeroConnectionPanel from "@/components/XeroConnectionPanel";

// ── Constants ─────────────────────────────────────────────────────────────────
const BOOKKEEPERS = [
  { name: "Jany L.",    email: "lopez.jany@gmail.com" },
  { name: "Irit P.",    email: "iritna@gmail.com" },
  { name: "Yunuen A.",  email: "yunuenalfaro7@gmail.com" },
  { name: "Shobhit A.", email: "shobhit@advancefinserv.com" },
  { name: "Lulu M.",    email: "lourdesmastache20@gmail.com" },
  { name: "Marian H.",  email: "marianjoyhernandez2@gmail.com" },
  { name: "Renata L.",  email: "erenatalazo@gmail.com" },
  { name: "Arvind K.",  email: "arvind@advancefinserv.com" },
  // Assistant Controllers — approved to assist with Bookkeeping Tier assignments
  { name: "Grecia Soto", email: "grecia.ssoto13@gmail.com" },
  { name: "Anabel Diaz", email: "anabelldiazcoronado@msn.com" },
  // Managers — assignable to any tier when covering bookkeeper work
  { name: "Gus G.",     email: "gus@bbnfi.com" },
];

// T1/T2/T3 assignees — includes Jany + Gus as managers who occasionally cover these tiers
const T1_T3_BOOKKEEPERS = [
  ...BOOKKEEPERS.filter(b => b.name !== "Jany L."),
  { name: "Jany L.", email: "lopez.jany@gmail.com" },
];

// C-P&L assignees — manager, supervisor, controller, and approved Assistant Controllers.
const T4_BOOKKEEPERS = BOOKKEEPERS.filter(b =>
  b.name === "Jany L." || b.name === "Irit P." || b.name === "Gus G." ||
  b.email === "grecia.ssoto13@gmail.com" || b.email === "anabelldiazcoronado@msn.com"
);

// Special sentinel for Annual Review clients (suppresses monthly activity requirements)
const ANNUAL_SENTINEL = { name: "Annual", email: "__annual__" };

const TIER_META: Record<string, { label: string; code: string; color: string }> = {
  T1: { label: "B/Feed",   code: "B",   color: "oklch(0.55 0.16 85)" },   // yellow
  T2: { label: "Code",     code: "C",   color: "oklch(0.45 0.18 240)" },  // blue
  T3: { label: "Ano|T/B",  code: "ATB", color: "oklch(0.45 0.20 300)" },  // purple
  "T3-C": { label: "T3-C",  code: "PLB", color: "oklch(0.40 0.15 145)" },  // green
};

const STATUS_COLORS: Record<string, string> = {
  not_started: "oklch(0.45 0.02 240)",
  in_progress:  "oklch(0.55 0.16 85)",   // yellow (T1 default)
  complete:     "oklch(0.40 0.15 145)",  // green
  overdue:      "oklch(0.50 0.20 25)",   // red-orange
};
const STATUS_LABELS: Record<string, string> = {
  not_started: "Not Started",
  in_progress:  "In Progress",
  complete:     "Complete",
  overdue:      "Overdue",
};

const BK_COLORS = [
  "oklch(0.82 0.01 240)",
  "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)", "oklch(0.55 0.01 240)",
  "oklch(0.55 0.01 240)",  "oklch(0.55 0.01 240)",   "oklch(0.55 0.01 240)",
];

// ── Component ─────────────────────────────────────────────────────────────────
export default function BookkeepingManagerDashboard() {
  const { user } = useAuth();
  const effectiveRole = useEffectiveRole(user?.role ?? "bookkeeper");
  const { isPreviewMode, exitPreview, setPreviewRole } = usePreview();
  const [showBkkprPicker, setShowBkkprPicker] = useState(false);
  const bkkprPickerRef = useRef<HTMLDivElement>(null);

  // Close picker on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (bkkprPickerRef.current && !bkkprPickerRef.current.contains(e.target as Node)) {
        setShowBkkprPicker(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);
  const [showNotify, setShowNotify] = useState(false);
  const [notifyDefaults, setNotifyDefaults] = useState<{ recipient?: string; title?: string; message?: string }>({});
  const [showFiles, setShowFiles] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [bkFilter, setBkFilter] = useState("all");
  const [tierFilter, setTierFilter] = useState("all");
  const [editingNotes, setEditingNotes] = useState<Record<string, string>>({});
  const [expandedDrive, setExpandedDrive] = useState<string | null>(null);
  const [expandedBkCard, setExpandedBkCard] = useState<string | null>(null);
  const [contactModal, setContactModal] = useState<{ clientName: string; clientEmail?: string | null; clientPhone?: string | null } | null>(null);
  const [editingContact, setEditingContact] = useState<string | null>(null);
  const [editContactEmail, setEditContactEmail] = useState("");
  const [editContactPhone, setEditContactPhone] = useState("");
  const [editContactNotes, setEditContactNotes] = useState("");
  const [globalDueDate, setGlobalDueDate] = useState("");


  // Add client form
  const [newClientName, setNewClientName] = useState("");
  const [showAddClient, setShowAddClient] = useState(false);

  const utils = trpc.useUtils();

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: allClients = [], isLoading: clientsLoading } = trpc.clientStatus.listAll.useQuery(undefined, { refetchInterval: 5000 });
  const { data: allTierTasks = [], isLoading: tierLoading, dataUpdatedAt: tierDataUpdatedAt } = trpc.tierTasks.list.useQuery({}, { refetchInterval: 5000 });
  const { data: t4Stats } = trpc.tierTasks.t4Stats.useQuery(undefined, { refetchInterval: 5000 });
  const { data: tierRecon } = trpc.tierTasks.reconciliation.useQuery(undefined, { refetchInterval: 10_000, staleTime: 5_000 });
  // ── Live bookkeeper list from DB (SOP: auto-includes new bookkeepers added via Team & Access) ──
  const { data: liveBookkeepers = [] } = trpc.team.listBookkeepers.useQuery(undefined, { refetchInterval: 60_000, staleTime: 30_000 });
  // Assistant Controllers are approved to assist across T1 through C-P&L. They are not
  // returned by team.listBookkeepers, so merge their active staff records specifically
  // into the tier-assignment list without changing bookkeeper-only reports or previews.
  const { data: activeStaff = [] } = trpc.team.list.useQuery(undefined, { refetchInterval: 60_000, staleTime: 30_000 });
  const assistantControllers = useMemo(
    () => activeStaff.filter((member) => member.role === "asst_controller"),
    [activeStaff]
  );
  const tierAssignmentMembers = useMemo(() => {
    const baseMembers = liveBookkeepers.length > 0 ? liveBookkeepers : T1_T3_BOOKKEEPERS;
    return [...baseMembers, ...assistantControllers].filter(
      (member, index, members) => members.findIndex((candidate) => candidate.email === member.email) === index
    );
  }, [liveBookkeepers, assistantControllers]);
  const cplAssignmentMembers = useMemo(() => {
    const cplBookkeepers = liveBookkeepers.length > 0
      ? liveBookkeepers.filter((member) => member.role === "bookkeeping_manager" || member.role === "bookkeeper_supervisor")
      : T4_BOOKKEEPERS.filter((member) => member.email !== "gus@bbnfi.com");
    const gus = BOOKKEEPERS.find((member) => member.email === "gus@bbnfi.com");
    return [...cplBookkeepers, ...assistantControllers, ...(gus ? [gus] : [])].filter(
      (member, index, members) => members.findIndex((candidate) => candidate.email === member.email) === index
    );
  }, [liveBookkeepers, assistantControllers]);
  const PIPELINE_COLS = [
    { key: "nys",  label: "NYS",   value: tierRecon ? String(tierRecon.nys)  : "…", color: "oklch(0.45 0.02 240)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t1",   label: "T1",    value: tierRecon ? String(tierRecon.t1)   : "…", color: "oklch(0.55 0.14 75)",  numColor: "oklch(0.12 0.02 240)" },
    { key: "t2",   label: "T2",    value: tierRecon ? String(tierRecon.t2)   : "…", color: "oklch(0.45 0.15 240)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t3",   label: "T3",    value: tierRecon ? String(tierRecon.t3)   : "…", color: "oklch(0.45 0.18 300)", numColor: "oklch(0.12 0.02 240)" },
    { key: "t3c",  label: "C-P&L", value: tierRecon ? String((tierRecon as any).t3c ?? 0) : "…", color: "oklch(0.40 0.14 145)", numColor: "oklch(0.45 0.16 145)" },
  ] as const;

  const isLoading = clientsLoading || tierLoading;
  const myAssignedTasks = useMemo(
    () => allTierTasks.filter((task) => task.assigneeEmail?.toLowerCase() === user?.email?.toLowerCase()),
    [allTierTasks, user?.email]
  );

  const assignTier = trpc.tierTasks.assign.useMutation({
    onSuccess: () => { utils.tierTasks.list.invalidate(); toast.success("Assignment updated"); },
    onError: (err) => toast.error("Assignment failed", { description: err.message }),
  });

  const updateTierStatus = trpc.tierTasks.updateStatus.useMutation({
    // Optimistic update: immediately reflect status change in UI before server confirms
    onMutate: async (vars) => {
      await utils.tierTasks.list.cancel();
      const prev = utils.tierTasks.list.getData({});
      utils.tierTasks.list.setData({}, old =>
        old?.map(t => t.id === vars.id ? { ...t, status: vars.status } : t)
      );
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) utils.tierTasks.list.setData({}, ctx.prev);
      toast.error("Failed to update status", { description: err.message });
    },
    onSettled: () => utils.tierTasks.list.invalidate(),
  });

  const updateClientStatus = trpc.clientStatus.update.useMutation({
    onSuccess: () => { utils.clientStatus.listAll.invalidate(); toast.success("Status updated"); },
    onError: (err) => toast.error("Failed to update status", { description: err.message }),
  });

  const addClientMutation = trpc.clientStatus.add.useMutation({
    onSuccess: (result) => {
      utils.clientStatus.listAll.invalidate();
      if (result?.id) {
        initTierTasks.mutate({ clientStatusId: result.id, clientName: newClientName.trim() });
      }
      setNewClientName("");
      setShowAddClient(false);
      toast.success("Client added — tier tasks created");
    },
    onError: (err) => toast.error("Failed to add client", { description: err.message }),
  });

  const initTierTasks = trpc.tierTasks.initForClient.useMutation({
    onSuccess: () => utils.tierTasks.list.invalidate(),
  });

  const setIndividualDueDate = trpc.tierTasks.setDueDate.useMutation({
    onMutate: async (vars) => {
      await utils.tierTasks.list.cancel();
      const prev = utils.tierTasks.list.getData({});
      utils.tierTasks.list.setData({}, old =>
        old?.map(t => t.id === vars.id ? { ...t, dueAt: vars.dueAt } : t)
      );
      return { prev };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) utils.tierTasks.list.setData({}, ctx.prev);
      toast.error("Failed to update due date");
    },
    onSettled: () => utils.tierTasks.list.invalidate(),
  });

  const setGlobalDueDateMutation = trpc.tierTasks.setGlobalDueDate.useMutation({
    onSuccess: () => { utils.tierTasks.list.invalidate(); toast.success("Due date applied to all clients"); },
    onError: (err) => toast.error("Failed to set due date", { description: err.message }),
  });



  const removeClientMutation = trpc.clientStatus.remove.useMutation({
    onSuccess: () => { utils.clientStatus.listAll.invalidate(); utils.tierTasks.list.invalidate(); toast.success("Client removed"); },
    onError: (err) => toast.error("Failed to remove client", { description: err.message }),
  });

  const setAnnual = trpc.clientStatus.setAnnual.useMutation({
    onSuccess: () => { utils.clientStatus.listAll.invalidate(); toast.success("Annual Review status updated"); },
    onError: (err) => toast.error("Failed to update Annual status", { description: err.message }),
  });

  const setWorkCycleOverride = trpc.clientStatus.setWorkCycleOverride.useMutation({
    onSuccess: (_result, variables) => {
      utils.clientStatus.listAll.invalidate();
      utils.tierTasks.list.invalidate();
      utils.tierTasks.reconciliation.invalidate();
      utils.tierTasks.t4Stats.invalidate();
      toast.success(variables.active ? "Client work cycle activated" : "Client returned to scheduled cycle");
    },
    onError: (err) => toast.error("Unable to change client work cycle", { description: err.message }),
  });

  // Inline contact edit mutation
  const updateContactMutation = trpc.clientContact.update.useMutation({
    onSuccess: () => {
      utils.clientStatus.listAll.invalidate();
      toast.success("Contact info saved");
      setEditingContact(null);
    },
    onError: (err) => toast.error("Failed to save contact", { description: err.message }),
  });

  const [editingHubIds, setEditingHubIds] = useState<Record<string, string>>({});
  const [editingEmails, setEditingEmails] = useState<Record<string, string>>({});
  const setClientHubId = trpc.clientStatus.setClientHubId.useMutation({
    onSuccess: () => { utils.clientStatus.listAll.invalidate(); toast.success("Client Hub ID saved"); },
    onError: (err) => toast.error("Failed to save Client Hub ID", { description: err.message }),
  });
  const updateEmail = trpc.bulkUpload.updateEmail.useMutation({
    onSuccess: () => { utils.clientStatus.listAll.invalidate(); toast.success("Client email saved"); },
    onError: () => toast.error("Failed to save email"),
  });

   // ── Task notification counts per bookkeeper (for Team Workload badges) ──────
  const bookkeeperEmails = tierAssignmentMembers.map(b => b.email);
  const { data: taskNotifCounts = {} } = trpc.taskNotifications.countByEmails.useQuery(
    { emails: bookkeeperEmails },
    { refetchInterval: 5000 }
  );

  // ── Derived stats ──────────────────────────────────────────────────
  const total = allClients.length;
  const annualCount = allClients.filter(c => c.isAnnual).length;
  // Bi-annual clients are active (reportable) in July (6), December (11), and January (0)
  const _bmCurrentMonth = new Date().getMonth();
  const _bmBiAnnualActive = [0, 6, 11].includes(_bmCurrentMonth);
  // In active months: include bi-annual clients in the reportable set; otherwise monthly only
  const monthlyClients = allClients.filter(c => !c.isAnnual || _bmBiAnnualActive || c.workCycleOverrideActive);
  const monthlyTotal = monthlyClients.length;

  // Build a lookup: clientStatusId → { T1, T2, T3, T3-C } tier task rows
  // (must be defined before clientStatusFromTiers)
  const tierByClient = useMemo(() => {
    const map: Record<number, Record<string, typeof allTierTasks[0]>> = {};
    for (const t of allTierTasks) {
      if (!map[t.clientStatusId]) map[t.clientStatusId] = {};
      map[t.clientStatusId][t.tier] = t;
    }
    return map;
  }, [allTierTasks]);

  // Aggregate client-level status from tier tasks.
  // complete = the independently assigned C-P&L task is complete.
  // overdue = any task is overdue
  // in_progress = any task is in_progress
  // not_started = no task has been acted on
  const clientStatusFromTiers = useMemo(() => {
    const result: Record<number, "complete" | "in_progress" | "overdue" | "not_started"> = {};
    for (const client of monthlyClients) {
      const tiers = Object.values(tierByClient[client.id] ?? {});
      if (tiers.length === 0) { result[client.id] = "not_started"; continue; }
      const cplComplete = tiers.some(t => t.tier === "T3-C" && t.status === "complete");
      if (cplComplete) { result[client.id] = "complete"; continue; }
      if (tiers.some(t => t.status === "overdue")) { result[client.id] = "overdue"; continue; }
      if (tiers.some(t => t.status === "in_progress" || t.status === "complete")) { result[client.id] = "in_progress"; continue; }
      result[client.id] = "not_started";
    }
    return result;
  }, [monthlyClients, tierByClient]);

  const complete = monthlyClients.filter(c => clientStatusFromTiers[c.id] === "complete").length;
  const inProgress = monthlyClients.filter(c => clientStatusFromTiers[c.id] === "in_progress").length;
  const overdue = monthlyClients.filter(c => clientStatusFromTiers[c.id] === "overdue").length;
  const notStarted = monthlyClients.filter(c => clientStatusFromTiers[c.id] === "not_started").length;
  const pct = monthlyTotal > 0 ? Math.round((complete / monthlyTotal) * 100) : 0;
  // Monthly-only C-P&L complete count (excludes annual clients) — for progress bar sub-text
  const monthlyClientIdSet = useMemo(() => new Set(monthlyClients.map(c => c.id)), [monthlyClients]);
  const monthlyT3cCount = useMemo(() =>
    new Set(
      allTierTasks
        .filter((t) => monthlyClientIdSet.has(t.clientStatusId) && t.tier === "T3-C" && t.status === "complete")
        .map((t) => t.clientStatusId)
    ).size
  , [allTierTasks, monthlyClientIdSet]);
  // Team workload per bookkeeper — count T1/T2/T3 tasks assigned (excludes Annual Review clients)
  const annualClientIds = useMemo(() => new Set(allClients.filter(c => c.isAnnual).map(c => c.id)), [allClients]);
  const workload = useMemo(() => {
    // Count each client ONCE at their highest tier, attributed to the bookkeeper
    // who owns that highest-tier task. Tier priority: T3-C > T3 > T2 > T1.
    // T3-C = P&L Done tier (formerly T4).
    // Scheduled clients activated by a Controller are included.
    // Effective tier priority for per-bookkeeper workload:
    //   C-P&L complete or active = priority 4
    //   T3 any active = priority 3
    //   T2 any active = T2 (priority 2)
    //   T1 any active = T1 (priority 1)
    //   not_started = 0 (not yet touched)
    const TIER_PRIORITY: Record<string, number> = { "T3-C": 4, T3: 3, T2: 2, T1: 1 };
    const effectiveTierPriority = (t: typeof allTierTasks[0]): number => {
      if (t.status !== "not_started") return TIER_PRIORITY[t.tier] ?? 0;
      return 0;
    };
    const clientHighestTier = new Map<number, typeof allTierTasks[0]>();
    for (const t of allTierTasks) {
      const existing = clientHighestTier.get(t.clientStatusId);
      const tPriority = effectiveTierPriority(t);
      if (tPriority > 0 && (!existing || tPriority > effectiveTierPriority(existing))) {
        clientHighestTier.set(t.clientStatusId, t);
      }
    }

    // Use the tier-assignment list so workload cards include Assistant Controller work.
    const allStaff = tierAssignmentMembers;

    return allStaff.map((bk, i) => {
      // Clients where this bookkeeper owns the highest-tier task
      const myClients: typeof allTierTasks[0][] = [];
      clientHighestTier.forEach((task) => {
        if (task.assigneeEmail === bk.email) myClients.push(task);
      });

      const t1 = myClients.filter(t => t.tier === "T1").length;
      const t2 = myClients.filter(t => t.tier === "T2").length;
      const t3 = myClients.filter(t => t.tier === "T3").length;
      const t3c = myClients.filter(t => t.tier === "T3-C").length;
      const done = myClients.filter(t => t.status === "complete").length;
      const od = myClients.filter(t => t.status === "overdue").length;
      // Sort clients: T3-C first, then T3, T2, T1; within tier sort by name
      const TIER_ORDER: Record<string, number> = { "T3-C": 0, T3: 1, T2: 2, T1: 3 };
      const sortedClients = [...myClients].sort((a, b) => {
        const tierDiff = (TIER_ORDER[a.tier] ?? 9) - (TIER_ORDER[b.tier] ?? 9);
        if (tierDiff !== 0) return tierDiff;
        return (a.clientName ?? "").localeCompare(b.clientName ?? "");
      });
      return {
        name: bk.name, email: bk.email,
        t1, t2, t3, t3c,
        total: myClients.length, complete: done, overdue: od,
        color: BK_COLORS[i % BK_COLORS.length],
        initials: bk.name.slice(0, 2).toUpperCase(),
        clients: sortedClients,
      };
    });
  }, [allTierTasks, annualClientIds, tierAssignmentMembers]);

  // Filtered client list
  const filteredClients = useMemo(() => {
    return allClients.filter(c => {
      const q = searchFilter.toLowerCase();
      const tiers = tierByClient[c.id] ?? {};
      const tierAssignees = Object.values(tiers).map(t => t.assigneeName.toLowerCase()).join(" ");
      const matchSearch = !q ||
        c.clientName.toLowerCase().includes(q) ||
        tierAssignees.includes(q) ||
        (c.notes ?? "").toLowerCase().includes(q);
      // "annual" filter isolates Annual Review clients; otherwise exclude them from status filters
      // Use tier-derived status (not the stale client_status.status field)
      const derivedStatus = clientStatusFromTiers[c.id] ?? "not_started";
      const matchStatus = statusFilter === "annual"
        ? c.isAnnual
        : statusFilter === "all"
          ? true
          : (!c.isAnnual || c.workCycleOverrideActive || _bmBiAnnualActive) && derivedStatus === statusFilter;
      const matchBk = bkFilter === "all" ||
        Object.values(tiers).some(t => t.assigneeEmail === [...tierAssignmentMembers, ANNUAL_SENTINEL].find(b => b.name === bkFilter)?.email);
      const matchTier = tierFilter === "all" ||
        (tiers[tierFilter] !== undefined);
      return matchSearch && matchStatus && matchBk && matchTier;
    });
  }, [allClients, tierByClient, clientStatusFromTiers, searchFilter, statusFilter, bkFilter, tierFilter, tierAssignmentMembers]);

  const getGreeting = () => {
    const h = new Date().getHours();
    return h < 12 ? "Good morning" : h < 17 ? "Good afternoon" : "Good evening";
  };
  // ── Render ─────────────────────────────────────────────────────────────────────────────
  return (
    <DashboardLayout>
      <div className="space-y-5 animate-fade-slide-up">

        {/* ── Preview Mode Banner ── */}
        {isPreviewMode && (
          <div
            className="flex items-center justify-between gap-3 px-4 py-2.5 rounded-lg"
            style={{
              background: "oklch(0.65 0.18 142 / 10%)",
              border: "1.5px solid oklch(0.65 0.18 142 / 35%)",
            }}
          >
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="oklch(0.40 0.18 142)" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span className="text-sm font-semibold" style={{ color: "oklch(0.35 0.18 142)" }}>
                Preview Mode — viewing as Bkkpr Mgr
              </span>
              <span className="text-xs" style={{ color: "oklch(0.45 0.12 142)" }}>
                (read-only view — your Controller session is active)
              </span>
            </div>
            <button
              onClick={exitPreview}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all"
              style={{
                background: "oklch(0.65 0.18 142 / 15%)",
                border: "1px solid oklch(0.65 0.18 142 / 40%)",
                color: "oklch(0.35 0.18 142)",
                cursor: "pointer",
              }}
            >
              ← Back to Controller
            </button>
          </div>
        )}

        {/* ── Header ── */}
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="apx-phase-badge">Bkkpr Mgr</span>
            <h1 className="text-2xl font-bold text-[oklch(0.12_0.04_240)] mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>
              {getGreeting()}, {user?.name}
            </h1>
            <p className="text-sm text-[oklch(0.08_0.01_240)] mt-0.5">
              {isLoading ? "Loading…" : `${complete} of ${monthlyTotal} monthly clients closed · ${overdue} overdue${annualCount > 0 ? ` · ${annualCount} annual` : ""}`}
            </p>
          </div>

          {/* Pipeline tile — NYS / T1 / T2 / T3 / C-P&L */}
          <div className="apx-card rounded-xl px-4 py-3 flex items-end gap-4">
            {PIPELINE_COLS.map((col) => (
              <div key={col.key} className="flex flex-col items-center gap-0.5">
                <span className="text-xs font-bold" style={{ color: col.color }}>{col.label}</span>
                <span className="text-base font-bold tabular-nums" style={{ color: col.numColor }}>{col.value}</span>
              </div>
            ))}
          </div>

          <div className="flex gap-2 flex-wrap">
            {/* Notify Client */}
            <button onClick={() => setContactModal({ clientName: "", clientEmail: null, clientPhone: null })}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Notify Client
            </button>
            {/* Notify Team */}
            <button onClick={() => { setNotifyDefaults({}); setShowNotify(true); }}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              Notify Team
            </button>
            <button onClick={() => setShowFiles(v => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              {showFiles ? "Hide Files" : "Send Files"}
            </button>
            <button onClick={() => setShowAddClient(v => !v)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
              </svg>
              Add Client
            </button>
            {/* View History — anchor link to Prior Month Completion History panel */}
            <a
              href="#history-panel"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              style={{ background: "oklch(0.45 0.12 240 / 10%)", border: "2px solid oklch(0.45 0.12 240 / 50%)", color: "oklch(0.30 0.12 240)", textDecoration: "none" }}
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              View History
            </a>
            {/* View as Bookkeeper — dropdown picker */}
            {!isPreviewMode && (
              <div className="relative" ref={bkkprPickerRef}>
                <button
                  onClick={() => setShowBkkprPicker(v => !v)}
                  className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
                  style={{
                    background: "oklch(0.55 0.18 250 / 12%)",
                    border: "2px solid oklch(0.55 0.18 250 / 60%)",
                    color: "oklch(0.30 0.18 250)",
                  }}
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  View as Bkkpr
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showBkkprPicker && (
                  <div
                    className="absolute right-0 top-full mt-1 z-50 rounded-lg shadow-xl overflow-hidden"
                    style={{
                      background: "oklch(0.98 0.005 240)",
                      border: "1px solid oklch(0.85 0.01 240)",
                      minWidth: "160px",
                    }}
                  >
                    <div className="px-3 py-2 text-xs font-bold uppercase tracking-wider" style={{ color: "oklch(0.50 0.02 240)", borderBottom: "1px solid oklch(0.90 0.01 240)" }}>
                      Select Bookkeeper
                    </div>
                    {BOOKKEEPERS.map(bk => (
                      <button
                        key={bk.email}
                        onClick={() => {
                          setPreviewRole("bookkeeper");
                          // Store selected bookkeeper email in sessionStorage so BookkeeperDashboard can filter
                          sessionStorage.setItem("previewBookkeeperEmail", bk.email);
                          sessionStorage.setItem("previewBookkeeperName", bk.name);
                          setShowBkkprPicker(false);
                        }}
                        className="w-full text-left px-3 py-2 text-sm transition-colors hover:bg-[oklch(0.93_0.01_240)]"
                        style={{ color: "oklch(0.15 0.04 240)" }}
                      >
                        {bk.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {myAssignedTasks.length > 0 && (
          <section className="apx-card rounded-xl p-4" aria-label="My assigned bookkeeping work">
            <div className="flex flex-wrap items-end justify-between gap-2">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-[oklch(0.55_0.12_240)]">My Assigned Work</p>
                <h2 className="font-serif text-xl font-semibold text-[oklch(0.12_0.04_240)]">Update your assigned tiers</h2>
                <p className="mt-1 text-sm text-muted-foreground">Your updates synchronize to the Bookkeeping Manager and Controller views.</p>
              </div>
              <span className="rounded-full border px-2 py-1 text-xs font-semibold">{myAssignedTasks.length} assigned tier{myAssignedTasks.length === 1 ? "" : "s"}</span>
            </div>
            <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
              {myAssignedTasks.map((task) => (
                <div key={task.id} className="rounded-lg border bg-background p-3">
                  <p className="text-sm font-semibold">{task.clientName}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{TIER_META[task.tier]?.label ?? task.tier} · {task.dueAt ? `Due ${new Date(task.dueAt).toLocaleDateString()}` : "No due date"}</p>
                  <select
                    aria-label={`Update ${task.clientName} ${task.tier} progress`}
                    value={task.status}
                    onChange={(event) => updateTierStatus.mutate({ id: task.id, status: event.target.value as "not_started" | "in_progress" | "complete" | "overdue", callerEmail: user?.email ?? "", callerName: user?.name ?? undefined })}
                    className="mt-3 w-full rounded-md border bg-background px-2 py-1.5 text-sm font-semibold"
                  >
                    {Object.entries(STATUS_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* ── File panel ── */}
        {showFiles && (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-sm font-semibold text-[oklch(0.08_0.01_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Send Files</h3>
              <FileDropZone />
            </div>

          </div>
        )}

        {/* ── Add Client panel ── */}
        {showAddClient && (
          <div className="apx-card rounded-xl p-5">
            <h3 className="text-sm font-semibold text-[oklch(0.08_0.01_240)] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Add New Client</h3>
            <p className="text-sm text-[oklch(0.08_0.01_240)] mb-3">
              T1–T3-C tier tasks will be created automatically, all defaulting to Jany. Assign tiers from the client roster below.
            </p>
            <form onSubmit={(e) => {
              e.preventDefault();
              if (!newClientName.trim()) return;
              addClientMutation.mutate({ clientName: newClientName.trim(), bookkeeper: "JANY" });
            }} className="flex flex-wrap gap-3 items-end">
              <div className="flex flex-col gap-1">
                <label className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider">Client Name</label>
                <input value={newClientName} onChange={e => setNewClientName(e.target.value)} placeholder="Business name…" required
                  className="px-3 py-1.5 rounded-lg text-sm outline-none w-56"
                  style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)" }} />
              </div>
              <button type="submit" disabled={addClientMutation.isPending}
                className="px-4 py-1.5 rounded-lg text-sm font-semibold"
                style={{ background: "oklch(0.82 0.01 240)", color: "oklch(0.1 0.02 85)", opacity: addClientMutation.isPending ? 0.6 : 1 }}>
                {addClientMutation.isPending ? "Adding…" : "+ Add Client"}
              </button>
            </form>
          </div>
        )}

        {/* ── KPI Bar ── */}
        <div className="grid grid-cols-2 xl:grid-cols-5 gap-3">
          {[
            { label: "Total Clients", value: total, color: "oklch(0.08 0.01 240)" },
            { label: "Annual Review", value: annualCount, color: "oklch(0.35 0.08 270)", clickFilter: "annual" },
            { label: "Complete", value: complete, color: "oklch(0.08 0.01 240)" },
            { label: "In Progress", value: inProgress, color: "oklch(0.08 0.01 240)" },
            { label: "Not Started", value: notStarted, color: "oklch(0.22 0.04 240)" },
          ].map(card => (
            <div key={card.label}
              className={`apx-card rounded-xl p-4 ${'clickFilter' in card ? 'cursor-pointer hover:ring-2 hover:ring-[oklch(0.35_0.08_270)/30%]' : ''}`}
              onClick={'clickFilter' in card ? () => setStatusFilter(statusFilter === card.clickFilter ? 'all' : card.clickFilter!) : undefined}>
              <p className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider mb-1">{card.label}</p>
              <p className="text-2xl font-bold" style={{ fontFamily: "'Playfair Display', serif", color: card.color }}>
                {isLoading ? "—" : card.value}
              </p>
            </div>
          ))}
        </div>

        {/* ── Prior Month Completion History ── */}
        <BookkeeperHistoryPanel title="Prior Month Completion History" id="history-panel" />

        {/* ── Two-column body ── */}
        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] gap-5 items-start">

          {/* ── LEFT: Client Roster with Tier Assignment Grid ── */}
          <div className="apx-card rounded-xl p-5">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <div className="mr-auto">
                <h3 className="text-base font-semibold text-[oklch(0.08_0.01_240)]" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Schedule & Task Assignment ({filteredClients.length})
                </h3>
                {tierDataUpdatedAt > 0 && (
                  <p className="text-[10px] mt-0.5" style={{ color: "oklch(0.45 0.12 145)" }}>
                    ✓ Last updated: {new Date(tierDataUpdatedAt).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })}
                  </p>
                )}
              </div>
              {/* Search */}
              <div className="relative">
                <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[oklch(0.30_0.03_240)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z" />
                </svg>
                <input value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                  placeholder="Search client or bookkeeper…"
                  className="pl-8 pr-3 py-1.5 rounded-lg text-sm outline-none w-48"
                  style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)" }} />
              </div>
              {/* Tier filter */}
              <select value={tierFilter} onChange={e => setTierFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-sm outline-none"
                style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)" }}>
                <option value="all">All Tiers</option>
                {Object.entries(TIER_META).map(([k, v]) => <option key={k} value={k}>{k} — {v.label}</option>)}
              </select>
              {/* Bookkeeper filter */}
              <select value={bkFilter} onChange={e => setBkFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-sm outline-none"
                style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)" }}>
                <option value="all">All Bkkpr</option>
                {tierAssignmentMembers.map(bk => <option key={bk.email} value={bk.name}>{bk.name}</option>)}
              </select>
              {/* Status filter */}
              <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
                className="px-2 py-1.5 rounded-lg text-sm outline-none"
                style={{ background: statusFilter === "annual" ? "oklch(0.35 0.08 270 / 12%)" : "oklch(0.93 0.008 240)", border: `2px solid ${statusFilter === "annual" ? "oklch(0.35 0.08 270 / 40%)" : "oklch(0.25 0.04 240)"}`, color: statusFilter === "annual" ? "oklch(0.25 0.08 270)" : "oklch(0.12 0.04 240)" }}>
                <option value="all">All Statuses</option>
                <option value="annual">★ Annual Review</option>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
              {/* Global due date setter */}
              <div className="flex items-center gap-1.5 ml-auto">
                <span className="text-sm text-[oklch(0.35_0.03_240)] uppercase tracking-wider whitespace-nowrap">Set All Due:</span>
                <input
                  type="date"
                  value={globalDueDate}
                  onChange={e => setGlobalDueDate(e.target.value)}
                  className="px-2 py-1.5 rounded-lg text-sm outline-none"
                  style={{ background: "oklch(0.93 0.008 240)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.12 0.04 240)", colorScheme: "light" }}
                />
                <button
                  onClick={() => {
                    if (!globalDueDate) return;
                    // Parse as local midnight to avoid UTC off-by-one in US timezones
                    const ts = new Date(globalDueDate + "T00:00:00").getTime();
                    setGlobalDueDateMutation.mutate({ dueAt: ts, callerEmail: user?.email ?? "" });
                  }}
                  disabled={!globalDueDate || setGlobalDueDateMutation.isPending}
                  className="px-3 py-1.5 rounded-lg text-sm font-semibold transition-all"
                  style={{ background: "oklch(0.92 0.005 240 / 15%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)", opacity: (!globalDueDate || setGlobalDueDateMutation.isPending) ? 0.5 : 1 }}>
                  {setGlobalDueDateMutation.isPending ? "Applying…" : "Apply"}
                </button>
                {globalDueDate && (
                  <button
                    onClick={() => { setGlobalDueDateMutation.mutate({ dueAt: null, callerEmail: user?.email ?? "" }); setGlobalDueDate(""); }}
                    className="px-2 py-1.5 rounded-lg text-sm transition-all"
                    style={{ background: "oklch(0.92 0.005 240 / 12%)", border: "2px solid oklch(0.25 0.04 240)", color: "oklch(0.08 0.01 240)" }}>
                    Clear
                  </button>
                )}
              </div>

            </div>



            {/* Column headers */}
            <div className="hidden lg:grid grid-cols-[4fr_1fr_1fr_1fr_1fr_28px] gap-2 px-3 pb-2 border-b"
              style={{ borderColor: "oklch(1 0 0 / 6%)" }}>
              {[
                { label: "Client", tip: "" },
                { label: "T1 — B/Feed", tip: "Bank Feed" },
                { label: "T2 — Code", tip: "Coding" },
                { label: "T3 — Ano|T/B", tip: "Anomalies / Trial Balance" },
                { label: "C-P&L", tip: "P&L / Balance Sheet" },
                { label: "", tip: "" },
              ].map(({ label, tip }) => (
                <span key={label} title={tip || undefined} className={`text-xs uppercase tracking-wide text-[oklch(0.30_0.03_240)] font-semibold whitespace-nowrap${tip ? " cursor-help underline decoration-dotted decoration-[oklch(0.45_0.04_240)] underline-offset-2" : ""}`}>{label}</span>
              ))}
            </div>

            {/* Client rows */}
            {isLoading ? (
              <div className="space-y-2 mt-2">{[1,2,3,4,5].map(i => <div key={i} className="h-14 rounded-lg animate-pulse" style={{ background: "oklch(0.93 0.008 240)" }} />)}</div>
            ) : filteredClients.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-sm text-[oklch(0.08_0.01_240)]">No clients match your filters.</p>
              </div>
            ) : (
              <div className="space-y-1 mt-2 max-h-[640px] overflow-y-auto pr-1">
                {filteredClients.map(client => {
                  const tiers = tierByClient[client.id] ?? {};
                  const derivedClientStatus = clientStatusFromTiers[client.id] ?? client.status;
                  const statusColor = STATUS_COLORS[derivedClientStatus] ?? STATUS_COLORS.not_started;
                  const noteVal = editingNotes[client.clientName] ?? client.notes ?? "";

                  const isAnnualClient = client.isAnnual;
                  const isWorkCycleOverrideActive = client.workCycleOverrideActive;
                  // Bi-annual clients are active in July (6), December (11), and January (0)
                  const currentMonth = new Date().getMonth(); // 0=Jan, 6=Jul, 11=Dec
                  const biAnnualActiveMonths = [0, 6, 11];
                  const isBiAnnualActive = isAnnualClient && (biAnnualActiveMonths.includes(currentMonth) || isWorkCycleOverrideActive);
                  const isBiAnnualInactive = isAnnualClient && !biAnnualActiveMonths.includes(currentMonth) && !isWorkCycleOverrideActive;
                  const canManageScheduledWork = ["super_admin", "admin", "controller"].includes(user?.role ?? "");

                  return (
                    <div key={client.id} className="space-y-0">
                      <div
                        className="grid grid-cols-[1fr_auto] lg:grid-cols-[4fr_1fr_1fr_1fr_1fr_28px] gap-2 items-center px-3 py-2.5 rounded-lg group"
                        style={{ background: isBiAnnualInactive ? "oklch(0.88 0.01 270 / 60%)" : isBiAnnualActive ? "oklch(0.90 0.02 270 / 40%)" : "oklch(0.93 0.008 240)", borderRadius: expandedDrive === client.clientName ? "0.5rem 0.5rem 0 0" : undefined, opacity: isBiAnnualInactive ? 0.75 : 1 }}>

                        {/* Client name + notes */}
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">{client.clientName}</p>
                            {(client as any).accountingPlatform === "quickbooks" && (
                              <span title="QuickBooks" className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: "oklch(0.55 0.18 145 / 15%)", color: "oklch(0.35 0.18 145)", border: "1px solid oklch(0.55 0.18 145 / 30%)", lineHeight: 1 }}>QB</span>
                            )}
                            {(client as any).accountingPlatform === "other" && (
                              <span title="Other platform" className="shrink-0 text-[9px] font-bold px-1 py-0.5 rounded" style={{ background: "oklch(0.55 0.05 240 / 15%)", color: "oklch(0.35 0.05 240)", border: "1px solid oklch(0.55 0.05 240 / 30%)", lineHeight: 1 }}>OTH</span>
                            )}
                            {isAnnualClient && (
                              <>
                                <span className="text-xs px-1.5 py-0.5 rounded-full shrink-0" style={{ background: isBiAnnualActive ? "oklch(0.35 0.14 145 / 15%)" : "oklch(0.35 0.08 270 / 15%)", color: isBiAnnualActive ? "oklch(0.30 0.14 145)" : "oklch(0.30 0.08 270)", border: isBiAnnualActive ? "1px solid oklch(0.35 0.14 145 / 30%)" : "1px solid oklch(0.35 0.08 270 / 30%)", fontSize: "10px", fontWeight: 600 }}>{isWorkCycleOverrideActive ? "Work Cycle · Active" : isBiAnnualActive ? "Scheduled · Active" : "Scheduled · Inactive"}</span>
                                {canManageScheduledWork && !biAnnualActiveMonths.includes(currentMonth) && (
                                  <button
                                    type="button"
                                    onClick={() => setWorkCycleOverride.mutate({ clientName: client.clientName, active: !isWorkCycleOverrideActive, callerEmail: user?.email ?? "" })}
                                    className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors"
                                    style={{ background: isWorkCycleOverrideActive ? "oklch(0.35 0.08 270 / 14%)" : "oklch(0.35 0.14 145 / 14%)", color: isWorkCycleOverrideActive ? "oklch(0.30 0.08 270)" : "oklch(0.30 0.14 145)", border: `1px solid ${isWorkCycleOverrideActive ? "oklch(0.35 0.08 270 / 35%)" : "oklch(0.35 0.14 145 / 35%)"}` }}
                                  >
                                    {isWorkCycleOverrideActive ? "Return to Schedule" : "Activate Work"}
                                  </button>
                                )}
                              </>
                            )}
                          </div>
                          <input
                            value={noteVal}
                            onChange={e => setEditingNotes(prev => ({ ...prev, [client.clientName]: e.target.value }))}
                            onBlur={e => {
                              const newNote = e.target.value.trim();
                              if (newNote !== (client.notes ?? "")) {
                                updateClientStatus.mutate({
                                  clientName: client.clientName,
                                  bookkeeperBankFeed: client.bookkeeperBankFeed,
                                  bookkeeperCoding: client.bookkeeperCoding,
                                  // Use tier-derived status (not stale client_status.status)
                                  status: (clientStatusFromTiers[client.id] ?? client.status) as "not_started" | "in_progress" | "complete" | "overdue",
                                  notes: newNote || undefined,
                                });
                              }
                            }}
                            placeholder="Note…"
                            className="text-sm text-[oklch(0.35_0.03_240)] bg-transparent outline-none w-full truncate placeholder:text-[oklch(0.25_0.03_240)] focus:text-[oklch(0.08_0.01_240)]"
                          />
                        </div>

                        {/* STATUS column removed — redundant, tier statuses show full picture */}

                        {/* T1, T2, T3, C-P&L tier cells */}
                        {(["T1", "T2", "T3", "T3-C"] as const).map(tier => {
                          const task = tiers[tier];
                          const meta = TIER_META[tier];
                          const isT3C = tier === "T3-C";
                          // Bi-annual clients: greyed out when inactive (not Jul/Dec/Jan), fully active otherwise
                          if (isBiAnnualInactive) {
                            return (
                              <div key={tier} className="flex flex-col gap-1 min-w-0" title="Bi-Annual client — tier work active in July, December & January only">
                                <div className="px-1.5 py-1 rounded text-xs font-semibold text-center" style={{ background: "oklch(0.35 0.08 270 / 8%)", border: "1px dashed oklch(0.35 0.08 270 / 30%)", color: "oklch(0.45 0.06 270)", opacity: 0.6 }}>
                                  {task?.assigneeName ?? "—"}
                                </div>
                                <div className="px-1.5 py-1 rounded text-xs text-center" style={{ background: "oklch(0.35 0.08 270 / 5%)", border: "1px solid oklch(0.35 0.08 270 / 20%)", color: "oklch(0.50 0.04 270)", opacity: 0.5 }}>
                                  Jul / Dec / Jan
                                </div>
                              </div>
                            );
                          }
                          if (!task) {
                            // No task record yet — show an "Assign" dropdown that creates tasks on first selection
                            // Assistant Controllers are approved for every Bookkeeping Tier, including C-P&L.
                            const liveTierBkkprs = isT3C ? cplAssignmentMembers : tierAssignmentMembers;
                            const tierBookkeepersForEmpty = liveTierBkkprs;
                            const emptyDropdownOptions = tier === "T1"
                              ? [...tierBookkeepersForEmpty, ANNUAL_SENTINEL]
                              : tierBookkeepersForEmpty;
                            return (
                              <div key={tier} className="flex flex-col gap-1 min-w-0">
                                <select
                                  value=""
                                  onChange={async e => {
                                    const selectedName = e.target.value;
                                    if (!selectedName) return;
                                    if (tier === "T1" && selectedName === "Annual") {
                                      setAnnual.mutate({ clientName: client.clientName, isAnnual: true });
                                      return;
                                    }
                                    // First init all tier tasks for this client, then assign
                                    await initTierTasks.mutateAsync({ clientStatusId: client.id, clientName: client.clientName });
                                    const bk = [...tierAssignmentMembers, ...cplAssignmentMembers, ANNUAL_SENTINEL].find(b => b.name === selectedName);
                                    if (bk && bk.email !== "__annual__") {
                                      // After init, find the newly created task and assign it
                                      const freshTasks = await utils.tierTasks.list.fetch({});
                                      const newTask = freshTasks.find(t => t.clientName === client.clientName && t.tier === tier);
                                      if (newTask) assignTier.mutate({ id: newTask.id, assigneeName: bk.name, assigneeEmail: bk.email, callerEmail: user?.email ?? "" });
                                    }
                                  }}
                                  className="px-1.5 py-1 rounded text-sm font-semibold outline-none w-full"
                                  style={{ background: `${meta.color}08`, border: `1px dashed ${meta.color}40`, color: `${meta.color}80` }}>
                                  <option value="">— Assign —</option>
                                  {emptyDropdownOptions.map(bk => (
                                    <option key={bk.email} value={bk.name}>{bk.name}</option>
                                  ))}
                                </select>
                                <select
                                  disabled
                                  value="not_started"
                                  className="px-1.5 py-1 rounded text-sm outline-none w-full opacity-40"
                                  style={{ background: `${STATUS_COLORS.not_started}10`, border: `1px solid ${STATUS_COLORS.not_started}25`, color: STATUS_COLORS.not_started }}>
                                  {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                                </select>
                              </div>
                            );
                          }
                          // Use tier-specific color for in_progress: T1=yellow, T2=blue, T3=purple, T3-C=green
                          const taskStatusColor = task.status === "in_progress"
                            ? meta.color
                            : (STATUS_COLORS[task.status] ?? STATUS_COLORS.not_started);
                          // T1 gets Annual option; Assistant Controllers are eligible for every tier, including C-P&L.
                          const tierBookkeepers = isT3C ? cplAssignmentMembers : tierAssignmentMembers;
                          // T1 also includes the Annual sentinel to mark a client as Annual Review
                          const dropdownOptions = tier === "T1"
                            ? [...tierBookkeepers, ANNUAL_SENTINEL]
                            : tierBookkeepers;
                          return (
                            <div key={tier} className="flex flex-col gap-1 min-w-0">
                              {/* Assignee dropdown */}
                              <select
                                value={client.isAnnual && tier === "T1" ? "Annual" : task.assigneeName}
                                onChange={e => {
                                  if (tier === "T1" && e.target.value === "Annual") {
                                    // Mark client as Annual Review
                                    setAnnual.mutate({ clientName: client.clientName, isAnnual: true });
                                    return;
                                  }
                                  const bk = [...tierAssignmentMembers, ...cplAssignmentMembers, ANNUAL_SENTINEL].find(b => b.name === e.target.value);
                                  if (bk && bk.email !== "__annual__") assignTier.mutate({ id: task.id, assigneeName: bk.name, assigneeEmail: bk.email, callerEmail: user?.email ?? "" });
                                }}
                                className="px-1.5 py-1 rounded text-sm font-semibold outline-none w-full"
                                style={{ background: `${meta.color}15`, border: `1px solid ${meta.color}30`, color: meta.color }}>
                                {dropdownOptions.map(bk => (
                                  <option key={bk.email} value={bk.name}>{bk.name}</option>
                                ))}
                              </select>
                              {/* Tier status — T3 shows "T3-C" instead of "Complete" (SOP) */}
                              <select
                                value={task.status}
                                onChange={e => updateTierStatus.mutate({ id: task.id, status: e.target.value as "not_started" | "in_progress" | "complete" | "overdue", callerEmail: user?.email ?? "", callerName: user?.name ?? undefined })}
                                className="px-1.5 py-1 rounded text-sm outline-none w-full"
                                style={{ background: `${taskStatusColor}10`, border: `1px solid ${taskStatusColor}25`, color: taskStatusColor }}>
                                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                              </select>
                              {/* Completed timestamp — shown when status is complete */}
                              {task.status === "complete" && task.completedAt && (
                                <div
                                  className="text-[10px] px-1 py-0.5 rounded"
                                  style={{ background: "oklch(0.40 0.15 145 / 10%)", color: "oklch(0.30 0.12 145)", border: "1px solid oklch(0.40 0.15 145 / 30%)", whiteSpace: "nowrap" }}
                                  title={`Completed: ${new Date(task.completedAt).toLocaleString()}`}
                                >
                                  ✓ {new Date(task.completedAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}{" "}
                                  <span style={{ opacity: 0.75 }}>{new Date(task.completedAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}</span>
                                </div>
                              )}
                              {/* Due date — inline per-tier date picker (individual override) */}
                              {(() => {
                                const dueEndOfDay = task.dueAt ? task.dueAt + (24 * 60 * 60 * 1000 - 1) : null;
                                const isOverdue = dueEndOfDay !== null && dueEndOfDay < Date.now() && task.status !== "complete";
                                const currentVal = task.dueAt
                                  ? new Date(task.dueAt).toISOString().split("T")[0]
                                  : "";
                                return (
                                  <div className="relative group/due flex items-center gap-0.5">
                                    <input
                                      type="date"
                                      defaultValue={currentVal}
                                      key={currentVal}
                                      onChange={e => {
                                        const val = e.target.value;
                                        const ts = val ? new Date(val + "T00:00:00").getTime() : null;
                        setIndividualDueDate.mutate({ id: task.id, dueAt: ts, callerEmail: user?.email ?? "" });
                                      }}
                                      className="text-xs rounded px-1 py-0.5 outline-none w-full cursor-pointer"
                                      style={{
                                        background: isOverdue
                                          ? "oklch(0.55 0.18 25 / 15%)"
                                          : task.dueAt
                                          ? "oklch(0.72 0.14 75 / 18%)"
                                          : "oklch(0.92 0.005 240 / 10%)",
                                        border: `1px solid ${isOverdue ? "oklch(0.55 0.18 25 / 40%)" : task.dueAt ? "oklch(0.72 0.14 75 / 40%)" : "oklch(0.5 0.02 240 / 20%)"}`,
                                        color: isOverdue
                                          ? "oklch(0.55 0.18 25)"
                                          : task.dueAt
                                          ? "oklch(0.45 0.10 75)"
                                          : "oklch(0.45 0.02 240)",
                                        colorScheme: "light",
                                      }}
                                      title={task.dueAt
                                        ? (isOverdue ? "Overdue — click to change date" : "Individual due date — click to change")
                                        : "Set individual due date for this tier"}
                                    />
                                    {task.dueAt && (
                                      <button
                                        onClick={() => setIndividualDueDate.mutate({ id: task.id, dueAt: null, callerEmail: user?.email ?? "" })}
                                        className="opacity-0 group-hover/due:opacity-100 transition-opacity shrink-0"
                                        title="Clear this due date"
                                        style={{ color: "oklch(0.45 0.02 240)", fontSize: "10px", lineHeight: 1 }}
                                      >✕</button>
                                    )}
                                  </div>
                                );
                              })()}
                            </div>
                          );
                        })}

                        {/* Drive + Remove */}
                        <div className="flex flex-col gap-1 items-center">
                          <button
                            onClick={() => setExpandedDrive(expandedDrive === client.clientName ? null : client.clientName)}
                            className="w-6 h-6 flex items-center justify-center rounded transition-opacity shrink-0"
                            style={{ color: expandedDrive === client.clientName ? "oklch(0.08 0.01 240)" : "oklch(0.35 0.02 240)" }}
                            title={`${expandedDrive === client.clientName ? "Hide" : "View"} Drive documents`}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
                            </svg>
                          </button>
                          {(effectiveRole === "admin" || effectiveRole === "super_admin" || effectiveRole === "controller") && (
                            <button
                              onClick={() => { if (confirm(`Remove ${client.clientName}?`)) removeClientMutation.mutate({ clientName: client.clientName }); }}
                              className="w-6 h-6 flex items-center justify-center rounded transition-opacity text-[oklch(0.30_0.03_240)] hover:text-[oklch(0.08_0.01_240)]"
                              title="Remove client">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          )}
                          <button
                            onClick={() => setContactModal({ clientName: client.clientName, clientEmail: client.clientEmail ?? null, clientPhone: client.clientPhone ?? null })}
                            className="w-6 h-6 flex items-center justify-center rounded transition-opacity"
                            style={{ color: "oklch(0.08 0.01 240)" }}
                            title="Contact client">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                          </button>
                          {/* Inline contact edit pencil */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (editingContact === client.clientName) {
                                setEditingContact(null);
                              } else {
                                setEditingContact(client.clientName);
                                setEditContactEmail(client.clientEmail ?? "");
                                setEditContactPhone(client.clientPhone ?? "");
                                setEditContactNotes(client.notes ?? "");
                              }
                            }}
                            className="w-6 h-6 flex items-center justify-center rounded transition-opacity"
                            style={{ color: editingContact === client.clientName ? "oklch(0.45 0.12 75)" : "oklch(0.35 0.02 240)" }}
                            title={editingContact === client.clientName ? "Close edit" : "Edit contact info"}>
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      {/* Inline contact edit strip */}
                      {editingContact === client.clientName && (
                        <div
                          className="px-3 py-3"
                          style={{ background: "oklch(0.96 0.012 75 / 40%)", border: "1px solid oklch(0.72 0.14 75 / 40%)", borderTop: "none" }}
                        >
                          <p className="text-sm font-semibold mb-2" style={{ color: "oklch(0.35 0.08 75)" }}>Edit Contact Info — {client.clientName}</p>
                          <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-2">
                              <label className="text-sm w-14 shrink-0" style={{ color: "oklch(0.28 0.04 240)" }}>Email</label>
                              <input
                                type="email"
                                value={editContactEmail}
                                onChange={(e) => setEditContactEmail(e.target.value)}
                                placeholder="client@example.com"
                                className="flex-1 px-2 py-1 rounded-md text-sm outline-none"
                                style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)" }}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-sm w-14 shrink-0" style={{ color: "oklch(0.28 0.04 240)" }}>Phone</label>
                              <input
                                type="tel"
                                value={editContactPhone}
                                onChange={(e) => setEditContactPhone(e.target.value)}
                                placeholder="+1 (310) 555-0100"
                                className="flex-1 px-2 py-1 rounded-md text-sm outline-none"
                                style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)" }}
                              />
                            </div>
                            <div className="flex items-start gap-2">
                              <label className="text-sm w-14 shrink-0 pt-1" style={{ color: "oklch(0.28 0.04 240)" }}>Notes</label>
                              <textarea
                                value={editContactNotes}
                                onChange={(e) => setEditContactNotes(e.target.value)}
                                placeholder="Internal notes about this client…"
                                rows={3}
                                className="flex-1 px-2 py-1 rounded-md text-sm outline-none resize-y"
                                style={{ background: "oklch(0.97 0.005 240)", border: "1.5px solid oklch(0.72 0.14 75 / 60%)", color: "oklch(0.12 0.04 240)", minHeight: "60px" }}
                              />
                            </div>
                            <div className="flex gap-2 mt-1">
                              <button
                                type="button"
                                disabled={updateContactMutation.isPending}
                                onClick={() => updateContactMutation.mutate({
                                  clientName: client.clientName,
                                  email: editContactEmail || null,
                                  phone: editContactPhone || null,
                                  notes: editContactNotes || null,
                                })}
                                className="px-3 py-1 rounded-md text-sm font-semibold transition-all"
                                style={{ background: "oklch(0.72 0.14 75)", color: "oklch(0.08 0.01 240)", opacity: updateContactMutation.isPending ? 0.6 : 1 }}
                              >
                                {updateContactMutation.isPending ? "Saving…" : "Save"}
                              </button>
                              <button
                                type="button"
                                onClick={() => setEditingContact(null)}
                                className="px-3 py-1 rounded-md text-sm font-medium transition-all"
                                style={{ background: "oklch(0.92 0.005 240 / 40%)", color: "oklch(0.28 0.04 240)", border: "1px solid oklch(0.25 0.04 240 / 30%)" }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Expandable Drive documents panel */}
                      {expandedDrive === client.clientName && (
                        <div className="rounded-b-lg overflow-hidden"
                          style={{ border: "1px solid oklch(0.82 0.02 240)", background: "oklch(0.97 0.005 85)", borderTop: "none" }}>
                          <div className="px-3 py-2 flex items-center gap-2" style={{ borderBottom: "1px solid oklch(0.88 0.01 240)", background: "oklch(0.93 0.008 240)" }}>
                            <svg className="w-3.5 h-3.5" style={{ color: "oklch(0.08 0.01 240)" }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M3 15l4.5-8h9L21 15M3 15h18M3 15l2 4h14l2-4" />
                            </svg>
                            <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>Drive Documents — {client.clientName}</span>
                            <button onClick={() => setExpandedDrive(null)} className="ml-auto text-[oklch(0.30_0.03_240)] hover:text-[oklch(0.08_0.01_240)] transition-colors">
                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                          <div className="p-3"><ClientDocuments clientName={client.clientName} /></div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>{/* end left: client roster */}

          {/* ── RIGHT: Team Workload + Schedule + Inbox ── */}
          <div className="xl:sticky xl:top-6 space-y-5">

            {/* Schedule & Task Summary — per-bookkeeper tier count cards */}
            <div className="apx-card rounded-xl p-5">
              <h3 className="text-base font-semibold text-[oklch(0.08_0.01_240)] mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                Schedule & Task Summary
              </h3>
              <p className="text-sm text-[oklch(0.30_0.03_240)] mb-3">Clients-Bkkpr Highest Tier</p>

              {/* ── T3 vs C-P&L Progress Bar ── */}
{(() => {
                const totalT3 = workload.reduce((s, bk) => s + bk.t3, 0);
                const totalT3C = workload.reduce((s, bk) => s + bk.t3c, 0);
                const grandTotal = totalT3 + totalT3C;
                const t3Pct = grandTotal > 0 ? Math.round((totalT3 / grandTotal) * 100) : 0;
                const t3cPct = grandTotal > 0 ? 100 - t3Pct : 0;
                // Per-bookkeeper breakdown for tooltips (exclude zero-count entries)
                const t3Breakdown = workload.filter(bk => bk.t3 > 0).map(bk => `${bk.name}: ${bk.t3}`);
                const t3cBreakdown = workload.filter(bk => bk.t3c > 0).map(bk => `${bk.name}: ${bk.t3c}`);
                return (
                  <div className="mb-4 px-1">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-xs font-semibold" style={{ color: TIER_META.T3.color }}>T3 In-Progress: {totalT3}</span>
                      <span className="text-xs font-semibold" style={{ color: TIER_META["T3-C"].color }}>C-P&L Completed: {totalT3C}</span>
                    </div>
                    <div className="relative h-4 rounded-full overflow-hidden flex" style={{ background: "oklch(0.91 0.01 240)" }}>
                      {totalT3 > 0 && (
                        <div className="group relative h-full" style={{ width: `${t3Pct}%`, minWidth: 28 }}>
                          <div
                            className="h-full flex items-center justify-center text-[10px] font-bold text-white transition-all duration-700 cursor-pointer w-full"
                            style={{ background: TIER_META.T3.color }}
                          >
                            {t3Pct > 12 ? `${t3Pct}%` : ""}
                          </div>
                          {/* T3 tooltip */}
                          <div className="absolute bottom-full left-0 mb-2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <div className="rounded-lg shadow-xl px-3 py-2.5 text-xs whitespace-nowrap" style={{ background: "oklch(0.12 0.04 240)", border: `1px solid ${TIER_META.T3.color}40`, minWidth: 160 }}>
                              <div className="font-bold mb-1.5" style={{ color: TIER_META.T3.color }}>T3 In-Progress — {totalT3} clients</div>
                              {t3Breakdown.length > 0
                                ? t3Breakdown.map((line, i) => (
                                    <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                                      <span style={{ color: "oklch(0.80 0.01 240)" }}>{line.split(":")[0]}</span>
                                      <span className="font-semibold" style={{ color: TIER_META.T3.color }}>{line.split(":")[1]}</span>
                                    </div>
                                  ))
                                : <div style={{ color: "oklch(0.60 0.01 240)" }}>No assignments</div>
                              }
                              <div className="mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${TIER_META.T3.color}30`, color: "oklch(0.55 0.02 240)" }}>
                                {t3Pct}% of T3/C-P&L total
                              </div>
                            </div>
                            <div className="w-2 h-2 rotate-45 ml-3" style={{ background: "oklch(0.12 0.04 240)", marginTop: -5 }} />
                          </div>
                        </div>
                      )}
                      {totalT3C > 0 && (
                        <div className="group relative h-full" style={{ width: `${t3cPct}%`, minWidth: 28 }}>
                          <div
                            className="h-full flex items-center justify-center text-[10px] font-bold text-white transition-all duration-700 cursor-pointer w-full"
                            style={{ background: TIER_META["T3-C"].color }}
                          >
                            {t3cPct > 12 ? `${t3cPct}%` : ""}
                          </div>
                          {/* C-P&L tooltip */}
                          <div className="absolute bottom-full right-0 mb-2 z-50 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                            <div className="rounded-lg shadow-xl px-3 py-2.5 text-xs whitespace-nowrap" style={{ background: "oklch(0.12 0.04 240)", border: `1px solid ${TIER_META["T3-C"].color}40`, minWidth: 160 }}>
                              <div className="font-bold mb-1.5" style={{ color: TIER_META["T3-C"].color }}>C-P&L Completed — {totalT3C} clients</div>
                              {t3cBreakdown.length > 0
                                ? t3cBreakdown.map((line, i) => (
                                    <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                                      <span style={{ color: "oklch(0.80 0.01 240)" }}>{line.split(":")[0]}</span>
                                      <span className="font-semibold" style={{ color: TIER_META["T3-C"].color }}>{line.split(":")[1]}</span>
                                    </div>
                                  ))
                                : <div style={{ color: "oklch(0.60 0.01 240)" }}>No completions yet</div>
                              }
                              <div className="mt-1.5 pt-1.5" style={{ borderTop: `1px solid ${TIER_META["T3-C"].color}30`, color: "oklch(0.55 0.02 240)" }}>
                                {t3cPct}% of T3/C-P&L total
                              </div>
                            </div>
                            <div className="w-2 h-2 rotate-45 ml-auto mr-3" style={{ background: "oklch(0.12 0.04 240)", marginTop: -5 }} />
                          </div>
                        </div>
                      )}
                      {grandTotal === 0 && (
                        <div className="w-full h-full flex items-center justify-center text-xs text-[oklch(0.50_0.02_240)]">No data</div>
                      )}
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[10px] text-[oklch(0.45_0.03_240)]">of {grandTotal} total T3/C-P&L clients</span>
                      {grandTotal > 0 && <span className="text-[10px] font-semibold" style={{ color: TIER_META["T3-C"].color }}>{t3cPct}% complete</span>}
                    </div>
                  </div>
                );
              })()}

              {/* Tier legend */}
              <div className="flex gap-3 mb-3">
                {Object.entries(TIER_META).map(([k, v]) => (
                  <span key={k} className="text-sm font-semibold px-1.5 py-0.5 rounded-full"
                    style={{ background: `${v.color}15`, color: v.color }}>
                    {k}={v.code}
                  </span>
                ))}
              </div>
              {/* Summary cards grid — all bookkeepers incl. Jany (excl. Gus Controller) */}
              <div className="grid grid-cols-2 gap-2 mb-3">
                {workload.filter(bk => bk.name !== "Gus G.").map((bk) => {
                  const isExpanded = expandedBkCard === bk.email;
                  return (
                    <div key={bk.email} className={`rounded-lg p-3 cursor-pointer transition-all duration-200 ${isExpanded ? "col-span-2" : ""}`}
                      style={{ background: isExpanded ? "oklch(0.91 0.012 240)" : "oklch(0.93 0.008 240)", border: `1px solid ${isExpanded ? bk.color : bk.color + "20"}`, boxShadow: isExpanded ? `0 0 0 2px ${bk.color}30` : undefined }}
                      onClick={() => setExpandedBkCard(isExpanded ? null : bk.email)}>
                      {/* Card header */}
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                          style={{ background: `${bk.color}20`, color: bk.color }}>
                          {bk.initials}
                        </div>
                        <span className="text-sm font-semibold text-[oklch(0.18_0.04_240)] truncate">{bk.name}</span>
                        <div className="ml-auto flex items-center gap-1">
                          {(taskNotifCounts[bk.email] ?? 0) > 0 && (
                            <span
                              title={`${taskNotifCounts[bk.email]} unacknowledged task request(s)`}
                              className="text-sm font-bold px-1.5 py-0.5 rounded-full"
                              style={{ background: "oklch(0.92 0.005 240 / 20%)", color: "oklch(0.08 0.01 240)", border: "2px solid oklch(0.25 0.04 240)" }}>
                              {taskNotifCounts[bk.email]}✉
                            </span>
                          )}
                          {bk.overdue > 0 && (
                            <span className="text-sm font-bold px-1 py-0.5 rounded"
                              style={{ background: "oklch(0.92 0.005 240 / 15%)", color: "oklch(0.08 0.01 240)" }}>
                              {bk.overdue}⚠
                            </span>
                          )}
                          {/* Expand/collapse chevron */}
                          <svg className={`w-3.5 h-3.5 ml-1 transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}
                            style={{ color: bk.color }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      {/* Tier bars */}
                      {(["T1", "T2", "T3", "T3-C"] as const).map(tier => {
                        const count = tier === "T1" ? bk.t1 : tier === "T2" ? bk.t2 : tier === "T3" ? bk.t3 : bk.t3c;
                        const meta = TIER_META[tier];
                        const maxCount = Math.max(...workload.map(w => tier === "T1" ? w.t1 : tier === "T2" ? w.t2 : tier === "T3" ? w.t3 : w.t3c), 1);
                        const pctBar = Math.round((count / maxCount) * 100);
                        return (
                          <div key={tier} className="flex items-center gap-1.5 mb-1">
                            <span className="text-sm font-bold w-8 shrink-0 whitespace-nowrap" style={{ color: meta.color }}>{tier}</span>
                            <div className="flex-1 h-1.5 rounded-full overflow-hidden" style={{ background: "oklch(0.94 0.018 220)" }}>
                              <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pctBar}%`, background: meta.color }} />
                            </div>
                            <span className="text-sm font-mono text-[oklch(0.08_0.01_240)] w-4 text-right">{count}</span>
                          </div>
                        );
                      })}
                      <div className="flex items-center justify-between mt-1.5 pt-1.5" style={{ borderTop: "1px solid oklch(1 0 0 / 6%)" }}>
                        <span className="text-sm text-[oklch(0.30_0.03_240)]">Total: {bk.t1 + bk.t2 + bk.t3 + bk.t3c}</span>
                        {bk.complete > 0 && <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>{bk.complete} done</span>}
                      </div>
                      {/* Expanded client list */}
                      {isExpanded && (
                        <div className="mt-3 pt-3" style={{ borderTop: `1px solid ${bk.color}30` }}
                          onClick={e => e.stopPropagation()}>
                          <p className="text-xs font-semibold mb-2" style={{ color: bk.color }}>
                            {bk.name}’s Assigned Clients ({bk.clients.length})
                          </p>
                          {bk.clients.length === 0 ? (
                            <p className="text-xs text-[oklch(0.45_0.02_240)]">No clients assigned at highest tier.</p>
                          ) : (
                            <div className="grid grid-cols-2 sm:grid-cols-3 gap-1">
                              {bk.clients.map((c, ci) => {
                                const tierMeta = TIER_META[c.tier as keyof typeof TIER_META];
                                const statusColor = c.status === "complete" ? "oklch(0.45 0.15 145)" : c.status === "overdue" ? "oklch(0.45 0.18 25)" : "oklch(0.45 0.08 240)";
                                return (
                                  <div key={ci} className="flex items-center gap-1.5 px-2 py-1 rounded"
                                    style={{ background: "oklch(0.95 0.005 240)" }}>
                                    <span className="text-[10px] font-bold px-1 rounded shrink-0"
                                      style={{ background: `${tierMeta?.color ?? "#888"}20`, color: tierMeta?.color ?? "#888" }}>
                                      {c.tier}
                                    </span>
                                    <span className="text-xs text-[oklch(0.18_0.04_240)] truncate flex-1" title={c.clientName ?? ""}>{c.clientName}</span>
                                    <span className="text-[10px] font-semibold shrink-0" style={{ color: statusColor }}>
                                      {c.status === "complete" ? "✓" : c.status === "overdue" ? "!" : ""}
                                    </span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Manager row — Gus (Controller) only */}
              <div className="space-y-1.5">
                {workload.filter(bk => bk.name === "Gus G.").map(bk => (
                  <div key={bk.email} className="flex items-center gap-3 px-3 py-2 rounded-lg" style={{ background: "oklch(0.93 0.008 240)", border: `1px solid oklch(0.35 0.08 270 / 15%)` }}>
                    <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold shrink-0"
                      style={{ background: "oklch(0.35 0.08 270 / 15%)", color: "oklch(0.25 0.08 270)" }}>
                      {bk.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-[oklch(0.18_0.04_240)]">
                          {bk.name}
                          <span className="ml-1 text-sm font-normal" style={{ color: "oklch(0.35 0.08 270)" }}>
                            ({bk.name === "Jany L." ? "Bkkpr Mgr" : "Controller"})
                          </span>
                        </span>
                        <span className="text-sm font-mono text-[oklch(0.08_0.01_240)]">{bk.t1 + bk.t2 + bk.t3 + bk.t3c} total</span>
                      </div>
                      <div className="flex gap-2 mt-0.5">
                        {bk.t1 > 0 && <span className="text-sm" style={{ color: TIER_META.T1.color }}>T1:{bk.t1}</span>}
                        {bk.t2 > 0 && <span className="text-sm" style={{ color: TIER_META.T2.color }}>T2:{bk.t2}</span>}
                        {bk.t3 > 0 && <span className="text-sm" style={{ color: TIER_META.T3.color }}>T3:{bk.t3}</span>}
                        {bk.t3c > 0 && <span className="text-sm" style={{ color: TIER_META["T3-C"].color }}>C-P&L:{bk.t3c}</span>}
                        {bk.overdue > 0 && <span className="text-sm font-semibold" style={{ color: "oklch(0.08 0.01 240)" }}>{bk.overdue} overdue</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

          </div>{/* end right column */}

        </div>{/* end two-column grid */}

        {/* ── BELOW TABLE: SMS (left) + All Communications (right) ── */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5 mt-5 items-stretch">
          <div className="apx-card rounded-xl overflow-hidden flex flex-col">
            <SmsCombinedPanel />
          </div>
          <div className="apx-card rounded-xl overflow-hidden p-4" style={{ minHeight: 420 }}>
            <AllCommunicationsFeed
              email={user?.email ?? ""}
              role={user?.role ?? "bookkeeping_manager"}
              userName={user?.name ?? "Jany L."}
              userRole={user?.role ?? "bookkeeping_manager"}
            />
          </div>
        </div>

      </div>

      {showNotify && (
        <SendNotificationModal
          onClose={() => { setShowNotify(false); setNotifyDefaults({}); }}
          defaultRecipient={notifyDefaults.recipient}
          defaultTitle={notifyDefaults.title}
          defaultMessage={notifyDefaults.message}
          defaultType="task"
        />
      )}

      {/* Contact Client Modal */}
      {contactModal && (
        <ContactClientModal
          open={!!contactModal}
          onClose={() => setContactModal(null)}
          clientName={contactModal.clientName}
          clientEmail={contactModal.clientEmail}
          clientPhone={contactModal.clientPhone}
          senderName={user?.name ?? "APX Team"}
          senderEmail={user?.email ?? ""}
          userRole={user?.role ?? "bookkeeping_manager"}
        />
      )}
    </DashboardLayout>
  );
}
